import ClinicMembers from '@/components/clinic/ClinicMembers';
import { Header, Sidebar } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { hasClinicAdminAccess, useAuth } from '@/contexts/AuthContext';
import ClinicUsageSection from '@/features/clinic-usage/ClinicUsageSection';
import { useToast } from '@/hooks/use-toast';
import type { LabProvider } from '@/lib/authModeApi';
import { Database, FlaskConical, Loader2, Save, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';

interface OrgSettings {
  auth_org_id?: string;
  store_audio_in_blob: boolean;
  enabled_task_types?: string[] | null;
  verkefni_rollout?: 'all' | 'beta' | 'disabled';
  require_pii_training: boolean;
  pii_daily_reminder: boolean;
  retention_days: number | null;
  detachment_days: number | null;
  default_lab_provider: LabProvider;
  hekla_referrals_enabled: boolean;
}

// Preset day values offered for the retention/detachment policies. "Off" maps to null.
export const POLICY_DAY_PRESETS = [1, 2, 3, 4, 5, 6, 7, 14, 30] as const;
const POLICY_OFF_VALUE = 'off';

// The Select primitive works in string space; these map to/from the nullable
// integer the backend expects (null = off, otherwise an int >= 1).
export const policyDaysToSelectValue = (days: number | null): string =>
  days == null ? POLICY_OFF_VALUE : String(days);

export const selectValueToPolicyDays = (value: string): number | null =>
  value === POLICY_OFF_VALUE ? null : Number.parseInt(value, 10);

const ClinicSettingsPage = () => {
  const { authOrgId, authOrgName, user } = useAuth();
  const { toast } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState<OrgSettings | null>(null);
  const [savedReferralsEnabled, setSavedReferralsEnabled] = useState(false);

  const currentOrgName = authOrgName ?? 'núverandi stöð';

  // Load current auth org settings on mount and when active org changes.
  useEffect(() => {
    const fetchOrgSettings = async () => {
      if (!authOrgId) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch('/api/auth/org-settings', {
          credentials: 'include',
          headers: {
            Accept: 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setSavedReferralsEnabled(data.hekla_referrals_enabled === true);
          setSettings({
            ...data,
            auth_org_id: typeof data.auth_org_id === 'string' ? data.auth_org_id : authOrgId,
            hekla_referrals_enabled: data.hekla_referrals_enabled === true,
            require_pii_training: data.require_pii_training === true,
            pii_daily_reminder: data.pii_daily_reminder === true,
            // Audio storage defaults to on unless explicitly false.
            store_audio_in_blob: data.store_audio_in_blob !== false,
            // An org without a settings row returns {}; treat missing policies as "off" (null).
            retention_days: typeof data.retention_days === 'number' ? data.retention_days : null,
            detachment_days: typeof data.detachment_days === 'number' ? data.detachment_days : null,
            default_lab_provider: data.default_lab_provider === 'landspitali' ? 'landspitali' : 'sameind',
          });
        } else {
          toast({
            title: 'Villa',
            description: 'Ekki tókst að sækja stillingar stöðvar.',
            variant: 'destructive',
          });
        }
      } catch (error) {
        console.error('Error fetching organization settings:', error);
        toast({
          title: 'Villa',
          description: 'Ekki tókst að sækja stillingar stöðvar.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrgSettings();
  }, [authOrgId, toast]);

  const handleSaveSettings = async () => {
    if (!settings) return;

    setIsSaving(true);
    try {
      const response = await fetch('/api/auth/org-settings', {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          ...(settings.hekla_referrals_enabled !== savedReferralsEnabled
            ? { hekla_referrals_enabled: settings.hekla_referrals_enabled }
            : {}),
          require_pii_training: settings.require_pii_training,
          pii_daily_reminder: settings.pii_daily_reminder,
          store_audio_in_blob: settings.store_audio_in_blob,
          retention_days: settings.retention_days,
          detachment_days: settings.detachment_days,
          default_lab_provider: settings.default_lab_provider,
        }),
      });

      if (response.ok) {
        setSavedReferralsEnabled(settings.hekla_referrals_enabled);
        toast({
          title: 'Vistað',
          description: 'Stillingar stöðvar hafa verið uppfærðar.',
        });
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving clinic settings:', error);
      toast({
        title: 'Villa',
        description: 'Ekki tókst að vista stillingar stöðvar.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!authOrgId) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex flex-1 flex-col">
          <Header title="Stillingar stöðvar" />
          <main className="flex-1 p-6">
            <div className="mx-auto max-w-4xl">
              <Card>
                <CardContent className="flex items-center justify-center py-12">
                  <p className="text-muted-foreground">Engin virk stöð fannst fyrir innskráðan aðgang.</p>
                </CardContent>
              </Card>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex flex-1 flex-col">
        <Header title="Stillingar stöðvar" />
        <main className="flex-1 p-6">
          <div className="mx-auto max-w-4xl space-y-6">
            {/* Header */}
            <div>
              <h1 className="font-bold text-3xl tracking-tight">Stillingar stöðvar</h1>
              <p className="text-muted-foreground">Stjórna stillingum fyrir {currentOrgName}</p>
            </div>

            {isLoading ? (
              <Card>
                <CardContent className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </CardContent>
              </Card>
            ) : settings ? (
              <div className="space-y-6">
                {/* PII Training Settings */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-5 w-5 text-primary" />
                      <CardTitle>Persónuvernd og fræðsla</CardTitle>
                    </div>
                    <CardDescription>Stillingar tengdar persónuverndarfræðslu og þjálfun notenda</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="require_pii_training"
                        checked={settings.require_pii_training}
                        onCheckedChange={(checked) =>
                          setSettings({ ...settings, require_pii_training: checked as boolean })
                        }
                        className="mt-1 cursor-pointer"
                      />
                      <div className="space-y-1">
                        <Label
                          htmlFor="require_pii_training"
                          className="-mt-0.5 cursor-pointer font-medium text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Krefjast fræðslu um persónugreinanlegar upplýsingar fyrir notendur
                        </Label>
                        <p className="text-muted-foreground text-sm">
                          Þegar hakað er í boxið verða notendur stöðvarinnar að ljúka stuttri fræðslu um
                          persónugreinanlegar upplýsingar áður en þeir geta notað Skríbuna.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="pii_daily_reminder"
                        checked={settings.pii_daily_reminder}
                        onCheckedChange={(checked) =>
                          setSettings({ ...settings, pii_daily_reminder: checked as boolean })
                        }
                        className="mt-1 cursor-pointer"
                      />
                      <div className="space-y-1">
                        <Label
                          htmlFor="pii_daily_reminder"
                          className="-mt-0.5 cursor-pointer font-medium text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Dagleg áminning um persónugreinanlegar upplýsingar
                        </Label>
                        <p className="text-muted-foreground text-sm">
                          Þegar hakað er í boxið munu notendur stöðvarinnar fá sprettiglugga einu sinni á dag sem minnir
                          þá á að nota ekki persónugreinanlegar upplýsingar við hljóðritun.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Audio Storage */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Database className="h-5 w-5 text-primary" />
                      <CardTitle>Hljóðgeymsla</CardTitle>
                    </div>
                    <CardDescription>Stillingar tengdar geymslu hljóðupptaka</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="store_audio_in_blob"
                        checked={settings.store_audio_in_blob}
                        onCheckedChange={(checked) =>
                          setSettings({ ...settings, store_audio_in_blob: checked as boolean })
                        }
                        className="mt-1 cursor-pointer"
                      />
                      <div className="space-y-1">
                        <Label
                          htmlFor="store_audio_in_blob"
                          className="-mt-0.5 cursor-pointer font-medium text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Hljóðvistun
                        </Label>
                        <p className="text-muted-foreground text-sm">Vista hljóðupptökur</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Data Retention & Detachment */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Database className="h-5 w-5 text-primary" />
                      <CardTitle>Varðveisla og aftenging gagna</CardTitle>
                    </div>
                    <CardDescription>
                      Sjálfvirkar stefnur fyrir eldri upptökur stöðvarinnar. Keyrðar daglega.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="max-w-md space-y-2">
                      <Label htmlFor="retention_days">Eyðing gagna</Label>
                      <Select
                        value={policyDaysToSelectValue(settings.retention_days)}
                        onValueChange={(value) =>
                          setSettings({ ...settings, retention_days: selectValueToPolicyDays(value) })
                        }
                      >
                        <SelectTrigger id="retention_days" className="cursor-pointer">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="off">Slökkt</SelectItem>
                          {POLICY_DAY_PRESETS.map((days) => (
                            <SelectItem key={days} value={String(days)}>
                              {days} {days === 1 ? 'dagur' : 'dagar'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-muted-foreground text-sm">
                        Eyðir varanlega upptökum, samantektum, verkefnum og öðrum gögnum sem eru eldri en valinn fjöldi
                        daga. Þessa aðgerð er ekki hægt að taka til baka.
                      </p>
                    </div>
                    <div className="max-w-md space-y-2">
                      <Label htmlFor="detachment_days">Aftenging</Label>
                      <Select
                        value={policyDaysToSelectValue(settings.detachment_days)}
                        onValueChange={(value) =>
                          setSettings({ ...settings, detachment_days: selectValueToPolicyDays(value) })
                        }
                      >
                        <SelectTrigger id="detachment_days" className="cursor-pointer">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="off">Slökkt</SelectItem>
                          {POLICY_DAY_PRESETS.map((days) => (
                            <SelectItem key={days} value={String(days)}>
                              {days} {days === 1 ? 'dagur' : 'dagar'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-muted-foreground text-sm">
                        Aftengir notendur frá upptökum sem eru eldri en valinn fjöldi daga. Upptakan er varðveitt en
                        ekki lengur skráð á notandann sem tók hana upp. Þessa aðgerð er ekki hægt að taka til baka.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Tilvísanir í Heklu</CardTitle>
                    <CardDescription>Sending tilvísana úr verkefnalista</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="hekla-referrals"
                        checked={settings.hekla_referrals_enabled}
                        disabled={!hasClinicAdminAccess(user)}
                        onCheckedChange={(value) =>
                          setSettings({ ...settings, hekla_referrals_enabled: value === true })
                        }
                      />
                      <Label htmlFor="hekla-referrals">Virkja sendingu tilvísana</Label>
                    </div>
                  </CardContent>
                </Card>
                {/* Lab ordering */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <FlaskConical className="h-5 w-5 text-primary" />
                      <CardTitle>Rannsóknir</CardTitle>
                    </div>
                    <CardDescription>Stillingar tengdar pöntun blóðrannsókna</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="max-w-md space-y-2">
                      <Label htmlFor="default_lab_provider">Sjálfgefin rannsóknastofa</Label>
                      <Select
                        value={settings.default_lab_provider}
                        onValueChange={(value) =>
                          setSettings({ ...settings, default_lab_provider: value as LabProvider })
                        }
                      >
                        <SelectTrigger id="default_lab_provider" className="cursor-pointer">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sameind">Sameind</SelectItem>
                          <SelectItem value="landspitali">Landspítali</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-muted-foreground text-sm">
                        Rannsóknastofan sem blóðprufubeiðnir stöðvarinnar eru sendar til.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Notendur — clinic-admin user management */}
                {hasClinicAdminAccess(user) && <ClinicMembers />}

                {/* Notkun — clinic usage metrics */}
                <ClinicUsageSection />

                {/* Save Button */}
                <div className="flex justify-end">
                  <Button onClick={handleSaveSettings} disabled={isSaving}>
                    {isSaving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Vista...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Vista stillingar
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <Card>
                <CardContent className="flex items-center justify-center py-12">
                  <p className="text-muted-foreground">Ekki tókst að hlaða stillingum.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ClinicSettingsPage;
